/**
 * Device Information Utility
 * Captures browser, OS, screen, timezone, and location information
 */

export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  
  // Detect browser
  let browser = 'Unknown Browser';
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Edg')) {
    browser = 'Edge';
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    browser = 'Opera';
  }
  
  // Detect OS
  let os = 'Unknown OS';
  if (userAgent.includes('Win')) {
    os = 'Windows';
  } else if (userAgent.includes('Mac')) {
    os = 'macOS';
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
  } else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    os = 'iOS';
  }
  
  // Device type
  let deviceType = 'Desktop';
  if (/Mobile|Android|iPhone|iPad|iPod/i.test(userAgent)) {
    deviceType = 'Mobile';
  } else if (/Tablet|iPad/i.test(userAgent)) {
    deviceType = 'Tablet';
  }
  
  // Screen information
  const screenInfo = {
    width: window.screen.width,
    height: window.screen.height,
    availWidth: window.screen.availWidth,
    availHeight: window.screen.availHeight,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio || 1
  };
  
  // Timezone and locale
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || navigator.userLanguage;
  
  // Connection info (if available)
  let connectionType = 'Unknown';
  let effectiveType = 'Unknown';
  if (navigator.connection) {
    connectionType = navigator.connection.type || 'Unknown';
    effectiveType = navigator.connection.effectiveType || 'Unknown';
  }
  
  // Battery info (approximate - doesn't give exact percentage for privacy)
  let batterySupported = 'getBattery' in navigator;
  
  return {
    browser,
    browserVersion: getBrowserVersion(userAgent, browser),
    os,
    deviceType,
    screen: screenInfo,
    timezone,
    locale,
    connection: {
      type: connectionType,
      effectiveType: effectiveType
    },
    batterySupported,
    userAgent,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency || 'Unknown',
    maxTouchPoints: navigator.maxTouchPoints || 0
  };
};

const getBrowserVersion = (userAgent, browser) => {
  let version = 'Unknown';
  
  try {
    if (browser === 'Chrome') {
      const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (browser === 'Firefox') {
      const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (browser === 'Safari') {
      const match = userAgent.match(/Version\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (browser === 'Edge') {
      const match = userAgent.match(/Edg\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    }
  } catch (e) {
    version = 'Unknown';
  }
  
  return version;
};

export const getDeviceId = () => {
  // Try to get existing device ID from localStorage
  let deviceId = localStorage.getItem('device_id');
  
  if (!deviceId) {
    // Generate a new device ID based on various factors
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    
    const fingerprint = canvas.toDataURL();
    const screenFingerprint = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Create a simple hash-like device ID
    deviceId = btoa(`${fingerprint.slice(0, 50)}${screenFingerprint}${timezone}${navigator.userAgent}`).slice(0, 32);
    
    localStorage.setItem('device_id', deviceId);
  }
  
  return deviceId;
};

export const getDeviceName = () => {
  const info = getDeviceInfo();
  return `${info.browser} on ${info.os}`;
};
